import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, Typography, Paper, CircularProgress, Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { dashboardAPI, smartCardAPI } from '../services/api';
import { useAuth } from '../services/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  transition: 'transform 0.2s',
  cursor: 'pointer',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[8] },
}));

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [smartCardCount, setSmartCardCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const promises = [
      dashboardAPI.stats(),
      smartCardAPI.list().catch(() => ({ data: [] })),
    ];
    Promise.all(promises)
      .then(([statsRes, cardRes]) => {
        setStats(statsRes.data);
        const cards = cardRes.data.results || cardRes.data || [];
        setSmartCardCount(Array.isArray(cards) ? cards.length : 0);
      })
      .catch((err) => console.error('Dashboard load error:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const statCards = [
    { label: 'Total Smart Cards', value: stats?.total_smart_cards || 0, icon: <CreditCardIcon />, color: '#1976d2' },
  ];

  if (stats?.total_users !== undefined) {
    statCards.push({ label: 'Total Users', value: stats.total_users, icon: <PeopleIcon />, color: '#9c27b0' });
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Welcome, {user?.full_name?.split(' ')[0]}!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Smart Card Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={statCards.length > 4 ? 2.4 : 3} key={card.label}>
            <StyledCard onClick={() => navigate('/admin/smart-cards')}>
              <Box sx={{ color: card.color }}>{card.icon}</Box>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.label}</Typography>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CreditCardIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Generate New Smart Card
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a premium PVC-style gemstone identification smart card with QR verification, hologram, and security features.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="contained" size="large" startIcon={<CreditCardIcon />}
            onClick={() => navigate('/admin/smart-cards/generate')}>
            Generate New Card
          </Button>
          <Button variant="outlined" size="large" onClick={() => navigate('/admin/smart-cards')}>
            View All ({smartCardCount})
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
